import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import Button from "@/components/Base/Button";
import { FormSelect } from "@/components/Base/Form";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import { getToken } from "@/utils/auth";

type Team = { id: string; name: string; status?: boolean };
type Row = {
  productId: string;
  product: string;
  sku: string | null;
  productType?: string | null;
  opening: number;
  totalIn: number;
  totalOut: number;
  closing: number;
  inByColumn: Record<string, number>;
  outByColumn: Record<string, number>;
};
type MovementColumn = {
  key: string;
  date: string;
  ownerName: string;
};
const PRODUCT_TYPE_ORDER = ["Cigarette", "Device", "Refills", "Accessories"] as const;

function dateToYMD(v: Date) {
  return v.toISOString().slice(0, 10);
}

function formatDateHeading(ymd: string) {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
  }).format(d);
}

function intFmt(v: number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function renderIntCell(v: number | null | undefined) {
  return v === null || v === undefined ? "-" : intFmt(v);
}

function parseDateRangeInput(value: string): { startDate: string; endDate: string } | null {
  const m = value.trim().match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  return { startDate: m[1], endDate: m[2] };
}

export default function MasterStockDataPage() {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();

  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const defaultStart = dateToYMD(monthStart);
  const defaultEnd = dateToYMD(today);

  const [dateRange, setDateRange] = useState(`${defaultStart} - ${defaultEnd}`);
  const [teamId, setTeamId] = useState("");

  const [teams, setTeams] = useState<Team[]>([]);
  const [inColumns, setInColumns] = useState<MovementColumn[]>([]);
  const [outColumns, setOutColumns] = useState<MovementColumn[]>([]);
  const [rows, setRows] = useState<Row[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedTypeNames, setExpandedTypeNames] = useState<Set<string>>(new Set());

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/teams?pageSize=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list: Team[] = Array.isArray(res.data?.data) ? res.data.data : [];
        setTeams(list.filter((t) => t.status !== false).sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        // keep page usable without teams list
      }
    })();
  }, [apiUrl, token]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) {
        setError("Please select a valid date range.");
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);
      if (teamId) params.set("teamId", teamId);

      const res = await axios.get(`${apiUrl}/master-stock-data?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInColumns(Array.isArray(res.data?.inColumns) ? res.data.inColumns : []);
      setOutColumns(Array.isArray(res.data?.outColumns) ? res.data.outColumns : []);
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load Master Stock Data.");
      setInColumns([]);
      setOutColumns([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const colCount = useMemo(
    () => 4 + inColumns.length + 1 + outColumns.length + 1 + 1,
    [inColumns.length, outColumns.length]
  );
  const groupedRows = useMemo(() => {
    const groups = new Map<string, Row[]>();
    for (const r of rows) {
      const typeName = (r.productType || "Other").trim() || "Other";
      const arr = groups.get(typeName) ?? [];
      arr.push(r);
      groups.set(typeName, arr);
    }
    return Array.from(groups.entries()).sort((a, b) => {
      const ai = PRODUCT_TYPE_ORDER.indexOf(a[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
      const bi = PRODUCT_TYPE_ORDER.indexOf(b[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [rows]);

  useEffect(() => {
    setExpandedTypeNames((prev) => {
      const next = new Set(prev);
      for (const [typeName] of groupedRows) next.add(typeName);
      return next;
    });
  }, [groupedRows]);

  const buildTotals = (list: Row[]) => {
    const out = {
      opening: 0,
      totalIn: 0,
      totalOut: 0,
      closing: 0,
      inByColumn: Object.fromEntries(inColumns.map((c) => [c.key, 0])) as Record<string, number>,
      outByColumn: Object.fromEntries(outColumns.map((c) => [c.key, 0])) as Record<string, number>,
    };
    for (const r of list) {
      out.opening += Number(r.opening ?? 0);
      out.totalIn += Number(r.totalIn ?? 0);
      out.totalOut += Number(r.totalOut ?? 0);
      out.closing += Number(r.closing ?? 0);
      for (const c of inColumns) {
        out.inByColumn[c.key] += Number(r.inByColumn?.[c.key] ?? 0);
      }
      for (const c of outColumns) {
        out.outByColumn[c.key] += Number(r.outByColumn?.[c.key] ?? 0);
      }
    }
    return out;
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <div className="col-span-12">
            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Master Stock Data</div>
              {loading ? <Lucide icon="Loader2" className="w-4 h-4 animate-spin text-slate-500 md:ml-3" /> : null}
            </div>

            <div className="flex flex-col gap-8 mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="flex flex-col p-5 sm:flex-row sm:items-end gap-3">
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,0.7fr)_auto] gap-3 w-full sm:w-auto">
                    <div className="sm:w-[420px]">
                      <div className="text-xs text-slate-500 mb-1">Date Range</div>
                      <div className="relative inline-flex w-full">
                        <Lucide
                          icon="Calendar"
                          className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                        />
                        <Litepicker
                          value={dateRange}
                          onChange={(e) => setDateRange((e.target?.value || "").trim())}
                          options={{
                            autoApply: true,
                            singleMode: false,
                            numberOfColumns: 2,
                            numberOfMonths: 2,
                            showWeekNumbers: true,
                            dropdowns: {
                              minYear: 2020,
                              maxYear: null,
                              months: true,
                              years: true,
                            },
                            format: "YYYY-MM-DD",
                          }}
                          className="w-full min-w-[220px] pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                        />
                      </div>
                    </div>
                    <div className="sm:w-[170px]">
                      <div className="text-xs text-slate-500 mb-1">Team</div>
                      <FormSelect value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                        <option value="">All Teams</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </FormSelect>
                    </div>
                    <div className="sm:self-end">
                      <Button variant="primary" onClick={() => void loadData()} disabled={loading} className="whitespace-nowrap px-4">
                        Apply Filter
                      </Button>
                    </div>
                  </div>
                </div>

                {error ? <div className="px-5 pb-3 text-danger text-sm">{error}</div> : null}

              </div>

              {rows.length === 0 ? (
                <div className="flex flex-col box box--stacked">
                  <div className="px-5 py-6 text-slate-500 text-sm">
                    {loading ? "Loading..." : "No stock movement data found for selected filter."}
                  </div>
                </div>
              ) : (
                groupedRows.map(([typeName, typeRows]) => {
                  const expanded = expandedTypeNames.has(typeName);
                  const totals = buildTotals(typeRows);
                  return (
                    <div key={typeName} className="flex flex-col box box--stacked">
                      <div className="px-5 py-3 border-b border-slate-200/70 dark:border-darkmode-500">
                        <button
                          type="button"
                          className="w-full flex items-center gap-2 text-left"
                          onClick={() =>
                            setExpandedTypeNames((prev) => {
                              const next = new Set(prev);
                              if (next.has(typeName)) next.delete(typeName);
                              else next.add(typeName);
                              return next;
                            })
                          }
                        >
                          <Lucide icon={expanded ? "ChevronDown" : "ChevronRight"} className="w-4 h-4 text-slate-500" />
                          <span className="font-medium">{typeName}</span>
                          <span className="text-xs text-slate-500">({typeRows.length})</span>
                        </button>
                      </div>
                      {expanded ? (
                        <div className="max-w-full overflow-x-auto px-4 pb-4">
                          <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
                    <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                      <Table.Tr>
                        <Table.Td rowSpan={2} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] align-middle">
                          Product
                        </Table.Td>
                        <Table.Td rowSpan={2} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[220px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] align-middle">
                          SKU
                        </Table.Td>
                        <Table.Td rowSpan={2} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[350px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] text-right align-middle">
                          Opening
                        </Table.Td>
                        <Table.Td colSpan={inColumns.length + 1} className="py-2 px-2 font-medium text-center bg-success/20 dark:bg-success/20">
                          Stock IN
                        </Table.Td>
                        <Table.Td colSpan={outColumns.length + 1} className="py-2 px-2 font-medium text-center bg-warning/20 dark:bg-warning/20 border-l border-slate-200/80 dark:border-darkmode-400">
                          Stock OUT
                        </Table.Td>
                        <Table.Td rowSpan={2} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 border-l border-slate-200/80 dark:border-darkmode-400 min-w-[95px] text-right align-middle">
                          Closing
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        {inColumns.map((c) => (
                          <Table.Td key={`in-${c.key}`} className="py-2 px-2 text-center font-medium bg-success/10 dark:bg-success/10 min-w-[92px] whitespace-normal leading-tight">
                            <div className="text-[10px] text-slate-500">{c.ownerName}</div>
                            <div>{formatDateHeading(c.date)}</div>
                          </Table.Td>
                        ))}
                        <Table.Td className="py-2 px-2 text-center font-medium bg-success/30 dark:bg-success/30 min-w-[74px] whitespace-nowrap">
                          Total In
                        </Table.Td>
                        {outColumns.map((c) => (
                          <Table.Td key={`out-${c.key}`} className="py-2 px-2 text-center font-medium bg-warning/10 dark:bg-warning/10 min-w-[92px] whitespace-normal leading-tight border-l border-slate-200/80 dark:border-darkmode-400 first:border-l-0">
                            <div className="text-[10px] text-slate-500">{c.ownerName}</div>
                            <div>{formatDateHeading(c.date)}</div>
                          </Table.Td>
                        ))}
                        <Table.Td className="py-2 px-2 text-center font-medium bg-warning/30 dark:bg-warning/30 min-w-[74px] whitespace-nowrap">
                          Total Out
                        </Table.Td>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {typeRows.map((r) => (
                          <Table.Tr key={r.productId} className="[&_td]:border-b [&_td]:border-slate-200/60">
                            <Table.Td className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] truncate">
                              {r.product}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-[220px] z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] truncate">
                              {r.sku || "-"}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-right tabular-nums sticky left-[350px] z-20 bg-slate-50 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                              {intFmt(r.opening)}
                            </Table.Td>
                            {inColumns.map((c) => (
                              <Table.Td key={`${r.productId}-in-${c.key}`} className="py-2 px-2 text-center tabular-nums bg-success/10 dark:bg-success/10 min-w-[92px] whitespace-nowrap">
                                {renderIntCell(r.inByColumn?.[c.key])}
                              </Table.Td>
                            ))}
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-success/30 dark:bg-success/30 min-w-[74px] whitespace-nowrap">
                              {intFmt(r.totalIn)}
                            </Table.Td>
                            {outColumns.map((c) => (
                              <Table.Td key={`${r.productId}-out-${c.key}`} className="py-2 px-2 text-center tabular-nums bg-warning/10 dark:bg-warning/10 min-w-[92px] whitespace-nowrap border-l border-slate-200/80 dark:border-darkmode-400 first:border-l-0">
                                {renderIntCell(r.outByColumn?.[c.key])}
                              </Table.Td>
                            ))}
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/30 dark:bg-warning/30 min-w-[74px] whitespace-nowrap">
                              {intFmt(r.totalOut)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-right tabular-nums bg-slate-50/70 dark:bg-darkmode-700/40 border-l border-slate-200/80 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                              {intFmt(r.closing)}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      {typeRows.length > 0 ? (
                        <Table.Tr className="font-medium [&_td]:border-b [&_td]:border-slate-300/80 dark:[&_td]:border-darkmode-400">
                          <Table.Td className="py-2 px-2 bg-slate-100 dark:bg-darkmode-700 sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] whitespace-nowrap">
                            Total
                          </Table.Td>
                          <Table.Td className="py-2 px-2 bg-slate-100 dark:bg-darkmode-700 sticky left-[220px] z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] whitespace-nowrap">
                            -
                          </Table.Td>
                          <Table.Td className="py-2 px-2 text-right tabular-nums sticky left-[350px] z-20 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                            {intFmt(totals.opening)}
                          </Table.Td>
                          {inColumns.map((c) => (
                            <Table.Td key={`total-in-${c.key}`} className="py-2 px-2 text-center tabular-nums bg-success/20 dark:bg-success/20 min-w-[92px] whitespace-nowrap">
                              {intFmt(totals.inByColumn[c.key] ?? 0)}
                            </Table.Td>
                          ))}
                          <Table.Td className="py-2 px-2 text-center tabular-nums bg-success/40 dark:bg-success/40 min-w-[74px] whitespace-nowrap">
                            {intFmt(totals.totalIn)}
                          </Table.Td>
                          {outColumns.map((c) => (
                            <Table.Td key={`total-out-${c.key}`} className="py-2 px-2 text-center tabular-nums bg-warning/20 dark:bg-warning/20 min-w-[92px] whitespace-nowrap border-l border-slate-200/80 dark:border-darkmode-400 first:border-l-0">
                              {intFmt(totals.outByColumn[c.key] ?? 0)}
                            </Table.Td>
                          ))}
                          <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/40 dark:bg-warning/40 min-w-[74px] whitespace-nowrap">
                            {intFmt(totals.totalOut)}
                          </Table.Td>
                          <Table.Td className="py-2 px-2 text-right tabular-nums bg-slate-200/70 dark:bg-darkmode-700 border-l border-slate-200/80 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                            {intFmt(totals.closing)}
                          </Table.Td>
                        </Table.Tr>
                      ) : null}
                    </Table.Tbody>
                  </Table>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}

